/**
 * Fuel, measured in minutes of flying rather than kilograms: the game only ever needs to know how
 * long an aircraft can stay airborne, and minutes are what a crew reports on the radio.
 *
 * ASSUMPTIONS (see docs/sim-design.md): the final reserve follows ICAO Annex 6 — 30 minutes for
 * turbine aircraft, 45 for piston. Burn relative to level flight is a rough shape, not a fuel-flow
 * model. "Minimum fuel" means the crew can accept no further delay; below the reserve it becomes
 * MAYDAY FUEL, which is a distress call.
 */
import { distanceM, NM_TO_M, type AirportData } from '@atc/core';
import { isOnGroundPhase, type Aircraft } from './aircraft';
import { plannedPath } from './planned-path';
import type { Rng } from './rng';

export type FuelStateName = 'normal' | 'minimum' | 'mayday';

export interface FuelState {
  /** Minutes of fuel left at the current rate of burn. */
  enduranceMin: number;
  /** Minutes that should still be in the tanks at touchdown. */
  finalReserveMin: number;
  state: FuelStateName;
  /** Sim time the crew first told the controller about this state. */
  declaredAtS?: number;
  /** When the margin was last worked out; it walks the route, so it is not done every step. */
  checkedAtS?: number;
}

/** How often the margin to the runway is recomputed, in sim seconds. */
export const FUEL_CHECK_EVERY_S = 5;

export const FINAL_RESERVE_MIN = { turbine: 30, piston: 45 } as const;
/** Margin at or below which a crew tells the controller they have minimum fuel. */
export const MINIMUM_FUEL_MARGIN_MIN = 10;
/** Burn relative to level flight below 10 000 ft. */
export const BURN_FACTOR = { ground: 0.2, climb: 1.6, level: 1.15, hold: 1.0, descent: 0.5, final: 1.1 } as const;
/** Minutes added for the approach and landing itself. */
const LANDING_ALLOWANCE_MIN = 2;
/** Vectors are never a straight line; distance to a field without an approach is padded. */
const VECTOR_PADDING = 1.3;

const isPiston = (ac: Aircraft) => ac.category === 'light';
export const finalReserveFor = (ac: Aircraft): number => (isPiston(ac) ? FINAL_RESERVE_MIN.piston : FINAL_RESERVE_MIN.turbine);

export function burnFactorFor(ac: Aircraft): number {
  if (isOnGroundPhase(ac.phase)) return BURN_FACTOR.ground;
  if (ac.phase === 'final' || ac.phase === 'approach') return BURN_FACTOR.final;
  if (ac.nav.mode === 'hold') return BURN_FACTOR.hold;
  if (ac.verticalSpeedFpm > 300) return BURN_FACTOR.climb;
  if (ac.verticalSpeedFpm < -300) return BURN_FACTOR.descent;
  return BURN_FACTOR.level;
}

export function burnFuel(ac: Aircraft, dtS: number): void {
  if (!ac.fuel) return;
  ac.fuel.enduranceMin = Math.max(0, ac.fuel.enduranceMin - (dtS / 60) * burnFactorFor(ac));
}

/** Minutes to touchdown from here: along the planned path where there is one, else direct and padded. */
export function minutesToLand(ac: Aircraft, airport: AirportData): number {
  const gs = Math.max(90, ac.groundspeedKt);
  const path = plannedPath(ac, airport);
  let alongPath = 0;
  for (let i = 1; i < path.length; i++) alongPath += distanceM(path[i - 1]!.lat, path[i - 1]!.lon, path[i]!.lat, path[i]!.lon) / NM_TO_M;
  const direct = distanceM(ac.lat, ac.lon, airport.lat, airport.lon) / NM_TO_M;
  const nm = Math.max(alongPath, direct) * (ac.approach ? 1 : VECTOR_PADDING);
  return (nm / gs) * 60 + LANDING_ALLOWANCE_MIN;
}

/** Minutes of fuel beyond what it takes to land and keep the final reserve. Negative is a MAYDAY. */
export function fuelMarginMin(ac: Aircraft, airport: AirportData): number {
  return ac.fuel ? ac.fuel.enduranceMin - minutesToLand(ac, airport) - ac.fuel.finalReserveMin : Infinity;
}

export function fuelStateFor(marginMin: number): FuelStateName {
  if (marginMin < 0) return 'mayday';
  if (marginMin <= MINIMUM_FUEL_MARGIN_MIN) return 'minimum';
  return 'normal';
}

/**
 * Fuel on board when the flight appears. A normal flight carries its trip fuel, the final reserve,
 * an alternate and some contingency; a "tight" one is already down to the reserve and a few minutes,
 * which is what makes a hold or a long vector turn into a problem.
 */
export function initialFuel(ac: Aircraft, estimateMin: number, rng: Rng, tight = false): FuelState {
  const finalReserveMin = finalReserveFor(ac);
  const extra = tight ? rng.range(5, 15) : rng.range(20, 45) + rng.range(5, 10) + rng.range(0, 20);
  return { enduranceMin: estimateMin + finalReserveMin + extra, finalReserveMin, state: 'normal' };
}
